/**
 * Converts minutes to a time string in hh:mm format
 * @param minutes - Total minutes (e.g., 205)
 * @returns Time string in hh:mm format (e.g., "03:25")
 */
export const minutesToTimeString = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Converts a time string in hh:mm format to total minutes
 * @param timeString - Time string in hh:mm format (e.g., "03:25")
 * @returns Total minutes (e.g., 205)
 */
export const timeStringToMinutes = (timeString: string): number => {
  if (!timeString || !timeString.includes(':')) return 0;
  const [hours, minutes] = timeString.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return 0;
  return (hours * 60) + minutes;
};

/**
 * Formats minutes as a human-readable "Xh Ym" string (or just "Y min" under an hour).
 * Usato dai totali della pagina report e dal widget di fatturazione (T-017): stessa formattazione,
 * stessi minuti sorgente (calculateReportTotals.ts).
 */
export const formatMinutesToHoursMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours === 0) {
    return `${mins} min`;
  }
  return `${hours}h ${mins}m`;
};

/** 1 giornata lavorativa = 8 ore = 480 minuti (feedback ambiente di test 23/09/2026). */
export const WORKDAY_MINUTES = 480;

/**
 * Formatta i minuti come "gg:hh:mm" (giornate lavorative:ore:minuti), dove hh e' 0-7 e mm e' 0-59.
 * Usato solo per i valori FATTURATI (ore lavoro/viaggio); i valori registrati restano in "Xh Ym"
 * (formatMinutesToHoursMinutes) perche' devono restare confrontabili col dato della pagina report.
 */
export const formatMinutesToWorkdayFormat = (minutes: number): string => {
  const abs = Math.round(Math.abs(minutes));
  const days = Math.floor(abs / WORKDAY_MINUTES);
  const remainder = abs % WORKDAY_MINUTES;
  const hours = Math.floor(remainder / 60);
  const mins = remainder % 60;
  return `${days}:${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

export type WorkdayFormatParseResult =
  | { minutes: number; error?: undefined }
  | { minutes?: undefined; error: 'format' | 'range' };

/**
 * Parsa una stringa "gg:hh:mm" in minuti totali. Nessun valore negativo in input: e' una durata
 * fatturata, non uno scostamento. Distingue formato non riconosciuto da componenti fuori range
 * (hh 0-7, mm 0-59) cosi' il chiamante puo' mostrare un messaggio comprensibile.
 */
export const parseWorkdayFormatToMinutes = (value: string): WorkdayFormatParseResult => {
  const match = /^(\d+):(\d{1,2}):(\d{1,2})$/.exec(value.trim());
  if (!match) return { error: 'format' };
  const days = parseInt(match[1], 10);
  const hours = parseInt(match[2], 10);
  const mins = parseInt(match[3], 10);
  if (hours > 7 || mins > 59) return { error: 'range' };
  return { minutes: days * WORKDAY_MINUTES + hours * 60 + mins };
};
